import {
  ChangeDetectorRef,
  Directive,
  ErrorHandler,
  Optional,
  SkipSelf,
  Injectable,
  ViewContainerRef,
  Inject,
  Type,
  ComponentRef,
  ViewRef,
} from '@angular/core';
import {
  Router,
  RouterOutlet,
  Event,
  ActivatedRoute,
  ActivatedRouteSnapshot,
  RoutesRecognized,
  Route,
  GuardsCheckEnd,
  NavigationEnd,
} from '@angular/router';

import { Observable, Subject, switchMap } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

export type LoadableRoute2 = {
  loader?: {
    show: boolean;
    delay: Observable<any>;
  };
};

@Injectable({ providedIn: 'root' })
export class RouterPlaceholderService {
  public createdRoutesMap = new WeakMap<Route, DetachedRouterOutlet>();

  private pathsFromRoot: ActivatedRouteSnapshot[][] = [];

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {
    router.events
      .pipe(
        filter(event => event instanceof RoutesRecognized),
        switchMap(routesRecognizedEvent =>
          router.events.pipe(
            filter(event => event instanceof GuardsCheckEnd),
            take(1),
            map(() => <RoutesRecognized>routesRecognizedEvent),
          ),
        ),
      )
      .subscribe(({ state }) => {
        this.pathsFromRoot = this.getAllPathsFromRoot(state.root).map(path => {
          const trimmedPath = this.getPathFromLastRemainingActive(path);
          return this.proceesPath(trimmedPath);
        });
      });
  }

  public registerOutlet(outlet: DetachedRouterOutlet) {
    const { snapshot } = outlet.route;
    if (snapshot.routeConfig) {
      this.createdRoutesMap.set(snapshot.routeConfig, outlet);
    }

    this.pathsFromRoot = this.pathsFromRoot.map(path => this.proceesPath(path));
  }

  public unregisterOutlet(outlet: DetachedRouterOutlet) {
    const { snapshot } = outlet.route;
    if (snapshot.routeConfig) {
      this.createdRoutesMap.delete(snapshot.routeConfig);
    }
  }

  private getAllPathsFromRoot(route: ActivatedRouteSnapshot, acc: ActivatedRouteSnapshot[][] = []) {
    if (!route.firstChild) {
      acc.push(route.pathFromRoot);
      return acc;
    }

    route.children.forEach(child => this.getAllPathsFromRoot(child, acc));
    return acc;
  }

  private getPathFromLastRemainingActive(nextRoutes: ActivatedRouteSnapshot[]) {
    const reversedSnapshots = [...nextRoutes].reverse();
    const closestActivatedRouteIndex = reversedSnapshots.findIndex(snapshot => {
      if (!snapshot.routeConfig) return false;

      return this.createdRoutesMap.has(snapshot.routeConfig);
    });
    const closestActivatedRoute = reversedSnapshots[closestActivatedRouteIndex];
    if (!closestActivatedRoute) return [];

    const notActivatedRoutes = reversedSnapshots.slice(0, closestActivatedRouteIndex).reverse();
    return [closestActivatedRoute, ...notActivatedRoutes];
  }

  private proceesPath(nextRoutes: ActivatedRouteSnapshot[]) {
    const closestRouteIndex = nextRoutes.findIndex(snapshot => {
      if (!snapshot.routeConfig) return false;

      return this.createdRoutesMap.has(snapshot.routeConfig);
    });
    const closestRoute = nextRoutes[closestRouteIndex];
    if (!closestRoute) return nextRoutes;

    const notAcivatedRoutes = nextRoutes.slice(closestRouteIndex + 1);
    const willActivateRoute = notAcivatedRoutes[0];
    if (!willActivateRoute) return notAcivatedRoutes;

    const loader = (<LoadableRoute2>willActivateRoute.routeConfig)?.loader;
    if (loader && loader.show) {
      const outlet = this.createdRoutesMap.get(closestRoute.routeConfig!)!;
      outlet.createLoader();
    }
    return notAcivatedRoutes;
  }
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'rtc-detached-router-outlet',
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class DetachedRouterOutlet extends RouterOutlet {
  private context: Event[] = [];
  private loader: ComponentRef<any> | null = null;
  private ngOnDestroy$ = new Subject<void>();
  private detachedView: ViewRef | null = null;

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private errorHandler: ErrorHandler,
    private routerPlaceholderService: RouterPlaceholderService,
    private vcRef: ViewContainerRef,
    public route: ActivatedRoute,
    @Inject('ReportsRoutePlaceholderComponent') private reportsRoutePlaceholderComponent: Type<any>,
    @SkipSelf() @Optional() private parentOutlet: DetachedRouterOutlet,
  ) {
    super();

    router.events.pipe(takeUntil(this.ngOnDestroy$)).subscribe(event => {
      this.context = [event, ...this.context].slice(0, 50);
    });

    router.events.pipe(takeUntil(this.ngOnDestroy$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.removeLoader();
        this.reattachView();
      }
    });
  }

  public activateWith(): void {
    try {
      this.removeLoader();
      super.activateWith.apply(this, arguments);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info(this.context);
      this.errorHandler.handleError(e);
    }
    this.cdRef.detectChanges();
  }

  public ngOnInit() {
    this.routerPlaceholderService.registerOutlet(this);
    super.ngOnInit();
  }

  public ngOnDestroy() {
    this.routerPlaceholderService.unregisterOutlet(this);
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
    super.ngOnDestroy();
  }

  public createLoader() {
    if (this.loader) return;

    this.detachView();
    this.loader = this.vcRef.createComponent(this.reportsRoutePlaceholderComponent, { index: 0 });
    this.loader.changeDetectorRef.detectChanges();
    this.loader.onDestroy(() => this.loader?.location.nativeElement.remove());
  }

  public removeLoader() {
    if (!this.loader) return;

    this.loader.destroy();
    this.loader = null;
  }

  private detachView() {
    if (this.vcRef.length === 0) return;

    this.detachedView = this.vcRef.detach(0);
  }

  private reattachView() {
    if (!this.detachedView) return;
    if (this.detachedView.destroyed) {
      this.detachedView = null;
      return;
    }

    this.vcRef.insert(this.detachedView);
  }
}
